import React, { useEffect, useRef } from 'react';
import { StarIcon, CheckCircleIcon, ChevronRightIcon } from 'lucide-react';
import JsBarcode from 'jsbarcode';
interface LoyaltyData {
  memberId?: string;
  pointsBalance?: number;
  status?: 'active' | 'inactive';
  tier?: 'silver' | 'gold' | 'platinum';
  isEnrolled?: boolean;
}
interface LoyaltyCardProps {
  progress?: number;
  onJoinNow?: () => void;
  loyaltyData?: LoyaltyData;
  variant?: 'default' | 'compact';
}
export function LoyaltyCard({
  progress = 0,
  onJoinNow,
  loyaltyData,
  variant = 'default'
}: LoyaltyCardProps) {
  const barcodeRef = useRef<SVGSVGElement>(null);
  const isEnrolled = loyaltyData?.isEnrolled && progress === 100;
  // Generate barcode when in member state
  useEffect(() => {
    if (isEnrolled && loyaltyData?.memberId && barcodeRef.current) {
      try {
        JsBarcode(barcodeRef.current, loyaltyData.memberId, {
          format: 'CODE128',
          width: 2.5,
          height: 80,
          displayValue: false,
          background: '#ffffff',
          lineColor: '#000000',
          margin: 10
        });
      } catch (error) {
        console.error('Error generating barcode:', error);
      }
    }
  }, [isEnrolled, loyaltyData?.memberId]);
  const getTierColor = (tier?: string) => {
    switch (tier) {
      case 'platinum':
        return 'text-gray-300';
      case 'gold':
        return 'text-yellow-300';
      case 'silver':
        return 'text-gray-400';
      default:
        return 'text-blue-200';
    }
  };
  const getTierLabel = (tier?: string) => {
    if (!tier) return 'Member';
    return tier.charAt(0).toUpperCase() + tier.slice(1);
  };
  // Member Summary State
  if (isEnrolled) {
    if (variant === 'compact') {
      return <button onClick={onJoinNow} className="w-full bg-gradient-to-r from-[#1E2B55] to-[#2a3a6f] rounded-xl p-2.5 text-white shadow-md flex items-center justify-between" data-id="element-635" data-mp="29rkvpxqcg6b2">
          <div className="flex items-center gap-3" data-id="element-636" data-mp="7ywmn7r0qond">
            <div className="w-7 h-7 bg-[#FFD700] rounded-full flex items-center justify-center flex-shrink-0" data-id="element-637" data-mp="6hzodvqichl6">
              <StarIcon className="w-3.5 h-3.5 text-[#1E2B55]" fill="currentColor" data-id="element-638" data-mp="1imxmzwimqdb" />
            </div>
            <div className="text-left" data-id="element-639" data-mp="3spr2bo56u0ik">
              <p className="text-xs font-bold leading-tight" data-id="element-640" data-mp="3oodyfjlno7ea">Loyalty Points</p>
              <p className="text-sm font-black text-[#FFD700] leading-tight" data-id="element-641" data-mp="p8r3ldx6f27h">
                {loyaltyData?.pointsBalance?.toLocaleString() || '0'}
              </p>
            </div>
          </div>
          <div className="text-right" data-id="element-642" data-mp="kc0ey27b2yag">
            <p className="text-[10px] text-blue-200 uppercase font-bold tracking-wider leading-tight" data-id="element-643" data-mp="fhw437ux6643">
              {getTierLabel(loyaltyData?.tier)}
            </p>
            <ChevronRightIcon className="w-4 h-4 text-white ml-auto opacity-70" data-id="element-644" data-mp="17z8k8k4qi7ch" />
          </div>
        </button>;
    }
    return <div className="bg-gradient-to-r from-[#1E2B55] to-[#2a3a6f] rounded-xl p-4 text-white shadow-lg" data-id="element-645" data-mp="137gyqo0fqrt7">
        <div className="flex items-center gap-2 mb-4" data-id="element-646" data-mp="2lwl7bc7gu4wh">
          <div className="w-8 h-8 bg-[#FFD700] rounded-full flex items-center justify-center" data-id="element-647" data-mp="2qu38ggf8rftx">
            <StarIcon className="w-5 h-5 text-[#1E2B55]" fill="currentColor" data-id="element-648" data-mp="2uvyaxo8wrz9a" />
          </div>
          <div className="flex-1" data-id="element-649" data-mp="2zv1d90xi7rdk">
            <h3 className="text-base font-semibold" data-id="element-650" data-mp="2zw4458utttth">Loyalty Program</h3>
            <div className="flex items-center gap-1 mt-0.5" data-id="element-651" data-mp="34udyxicn2sek">
              <CheckCircleIcon className="w-3 h-3 text-green-400" data-id="element-652" data-mp="34tvx8677jlz9" />
              <span className="text-xs text-green-300" data-id="element-653" data-mp="39s75wdlrnqfw">Active Member</span>
            </div>
          </div>
        </div>
        {/* Member Info Grid */}
        <div className="grid grid-cols-2 gap-3 mb-4" data-id="element-654" data-mp="3embfl4mf2cko">
          <div className="bg-white bg-opacity-10 rounded-lg p-3 backdrop-blur-sm" data-id="element-655" data-mp="3ik94e6c9ywnj">
            <p className="text-xs text-blue-200 mb-1" data-id="element-656" data-mp="3nloo9v2pux4c">Points Balance</p>
            <p className="text-xl font-bold text-[#FFD700]" data-id="element-657" data-mp="3sjgr3k4wg3nb">
              {loyaltyData?.pointsBalance?.toLocaleString() || '0'}
            </p>
          </div>
          <div className="bg-white bg-opacity-10 rounded-lg p-3 backdrop-blur-sm" data-id="element-658" data-mp="3ev6o2cfcgl10">
            <p className="text-xs text-blue-200 mb-1" data-id="element-659" data-mp="39x50pfavxzjq">Membership Tier</p>
            <p className={`text-xl font-bold ${getTierColor(loyaltyData?.tier)}`} data-id="element-660" data-mp="3on2kzk5p253n">
              {getTierLabel(loyaltyData?.tier)}
            </p>
          </div>
        </div>
        {/* Status */}
        <div className="mb-4" data-id="element-661" data-mp="2vyx3d69xsw81">
          <div className="flex items-center justify-between text-xs" data-id="element-662" data-mp="2vzyne08xkt1w">
            <span className="text-blue-200" data-id="element-663" data-mp="2r0lrkq030bn6">Status</span>
            <span className={`font-semibold ${loyaltyData?.status === 'active' ? 'text-green-300' : 'text-red-300'}`} data-id="element-664" data-mp="35wunpiooe8vd">
              {loyaltyData?.status === 'active' ? 'Active' : 'Inactive'}
            </span>
          </div>
        </div>
        {/* Barcode Section */}
        {loyaltyData?.memberId && <div className="bg-white rounded-lg p-4 text-center" data-id="element-665" data-mp="g67vd6dwqh7i">
            <div className="flex items-center justify-center mb-3" data-id="element-666" data-mp="npz6gbruhmbv">
              <svg ref={barcodeRef} className="max-w-full h-auto" data-id="element-667" data-mp="nsv3zyzs68zs"></svg>
            </div>
            <p className="text-sm text-gray-700 font-mono tracking-wider" data-id="element-668" data-mp="snilvf0a455e">
              Member ID: {loyaltyData.memberId}
            </p>
          </div>}
      </div>;
  }
  // Onboarding State (existing behavior)
  if (variant === 'compact') {
    return <button onClick={onJoinNow} className="w-full bg-gradient-to-r from-[#1E2B55] to-[#2a3a6f] rounded-xl p-2.5 text-white shadow-md flex items-center justify-between" data-id="element-669" data-mp="16ib6usyxudjd">
        <div className="flex items-center gap-3" data-id="element-670" data-mp="xnwtwx1vrf3f">
          <div className="w-7 h-7 bg-[#FFD700] rounded-full flex items-center justify-center flex-shrink-0" data-id="element-671" data-mp="11q9y3h0zb4y3">
            <StarIcon className="w-3.5 h-3.5 text-[#1E2B55]" fill="currentColor" data-id="element-672" data-mp="1pg49du5nglg8" />
          </div>
          <div className="text-left" data-id="element-673" data-mp="1u7dxgqbjvgq0">
            <p className="text-xs font-bold leading-tight" data-id="element-674" data-mp="1u8fcf5vosnd0">
              Join Loyalty Program
            </p>
            <p className="text-[10px] text-blue-100 leading-tight mt-0.5" data-id="element-675" data-mp="200zkon6w7n3c">
              Earn points on every order
            </p>
          </div>
        </div>
        <ChevronRightIcon className="w-4 h-4 text-white opacity-70" data-id="element-676" data-mp="1v0blvfcfa1yk" />
      </button>;
  }
  return <div className="bg-gradient-to-r from-[#1E2B55] to-[#2a3a6f] rounded-xl p-4 text-white" data-id="element-677" data-mp="1q2jucmfowhuw">
      <div className="flex items-center gap-2 mb-3" data-id="element-678" data-mp="1l59vyhabovux">
        <div className="w-8 h-8 bg-[#FFD700] rounded-full flex items-center justify-center" data-id="element-679" data-mp="1gcgsdwqlbtkf">
          <StarIcon className="w-5 h-5 text-[#1E2B55]" fill="currentColor" data-id="element-680" data-mp="1cb5908yb84ko" />
        </div>
        <h3 className="text-base font-bold" data-id="element-681" data-mp="17cal4slhjptp">Loyalty Program</h3>
      </div>
      <p className="text-xs text-blue-100 mb-3" data-id="element-682" data-mp="17a7jfbaz0895">Profile {progress}% complete</p>
      <div className="mb-4" data-id="element-683" data-mp="12cpkmdqo39ev">
        <div className="flex justify-between items-center mb-2" data-id="element-684" data-mp="12f1x4bdnorkj">
          <span className="text-xs text-blue-100" data-id="element-685" data-mp="xeo1jst8ra3e">Progress</span>
          <span className="text-sm font-bold text-[#FFD700]" data-id="element-686" data-mp="naphxw2fc77i">{progress}%</span>
        </div>
        <div className="w-full h-2 bg-white bg-opacity-20 rounded-full overflow-hidden" data-id="element-687" data-mp="s8r974a07cu2">
          <div className="h-full bg-[#FFD700] transition-all duration-500" style={{
          width: `${progress}%`
        }} data-id="element-688" data-mp="x8n2o9l2rlvd" />
        </div>
      </div>
      <p className="text-xs text-blue-100 mb-3" data-id="element-689" data-mp="4jptcvyuczer">
        Complete your profile to unlock full benefits
      </p>
      <button onClick={onJoinNow} className="w-full bg-white text-[#1E2B55] text-sm font-bold py-2.5 px-4 rounded-lg hover:bg-gray-100 transition-colors" data-id="element-690" data-mp="9i9if58qerbc">
        {progress === 0 ? 'Join Now' : 'Complete Profile'}
      </button>
    </div>;
}